import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Calendar, Clock, ArrowRight, User } from "lucide-react";
import { PostsProvider, usePosts, type Post } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const getReadingTime = (content?: string): string => {
  if (!content) return "3 min read";
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const time = Math.ceil(words / wordsPerMinute);
  return `${time} min read`;
};
function PostItem({
  post,
  index
}: {
  post: Post;
  index: number;
}) {
  const readingTime = getReadingTime(post.content);
  const formattedDate = post.date ? new Date(post.date).toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric"
  }) : "";
  return <Link to={post.link || "#"} className="block h-full group" data-index={index}>
      <Card className="h-full overflow-hidden border-border/60 bg-card shadow-sm transition-all duration-300 hover:border-secondary/30 hover:shadow-xl hover:shadow-primary/5 flex flex-col">
        <div className="relative aspect-[16/9] overflow-hidden w-full">
          {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted/20 text-muted-foreground">
              <span className="text-sm">Consulting Insight</span>
            </div>}
          <div className="absolute top-4 left-4">
            <Badge variant="secondary" className="bg-secondary text-secondary-foreground border-none shadow-md hover:bg-secondary/90">
              {post.categories?.[0]?.name || "Expertise"}
            </Badge>
          </div>
        </div>

        <CardHeader className="space-y-3 pb-2">
          <div className="flex items-center gap-4 text-xs text-slate-500 font-medium tracking-wide">
            {formattedDate && <div className="flex items-center gap-1.5">
                <SafeHydrate><Calendar className="h-3.5 w-3.5" /></SafeHydrate>
                <span>{formattedDate}</span>
              </div>}
            <div className="flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" />
              <span>{readingTime}</span>
            </div>
          </div>
          <CardTitle className="font-sans text-xl font-bold leading-tight tracking-tight text-primary group-hover:text-secondary transition-colors line-clamp-2">
            {post.title}
          </CardTitle>
        </CardHeader>

        <CardContent className="pb-4">
          <CardDescription className="line-clamp-3 text-sm leading-relaxed text-slate-600">
            <div dangerouslySetInnerHTML={{
            __html: post.excerpt || ""
          }} />
          </CardDescription>
        </CardContent>

        <CardFooter className="mt-auto pt-0">
          <div className="flex w-full items-center justify-between border-t border-border/40 pt-4 mt-2">
            <div className="flex items-center gap-2 text-xs font-medium text-slate-500">
              <div className="h-6 w-6 rounded-full bg-secondary/10 flex items-center justify-center text-secondary">
                <User className="h-3.5 w-3.5" />
              </div>
              <span>{post.author?.name || "FocusPath Advisor"}</span>
            </div>
            <span className="flex items-center gap-1 text-xs font-bold text-secondary opacity-0 transition-all duration-300 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0">
              Read Analysis <ArrowRight className="h-3.5 w-3.5" />
            </span>
          </div>
        </CardFooter>
      </Card>
    </Link>;
}
function LatestInsightsConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div data-section-id="6825" className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="h-[420px] rounded-xl border border-border/40 bg-muted/20 animate-pulse" />)}
      </div>;
  }
  if (error) {
    return <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/10 rounded-xl border border-border/50 p-8">
        <p className="mb-4 text-muted-foreground">
          Unable to load our latest perspectives.
        </p>
        <Button onClick={refetch} variant="outline" className="border-secondary text-secondary hover:bg-secondary/5">
          Try Again
        </Button>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-12 text-center bg-muted/10 rounded-xl border border-border/50 p-8">
        <p className="text-muted-foreground">No recent publications found.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
      {posts.map((post, i) => <div key={post.id} data-index={i} className="h-full">
           <PostItem post={post} index={i} />
        </div>)}
    </div>;
}
export default function LatestInsights() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return;
}